import React, { useCallback } from 'react';
import {
    ApiResponse,
    InfluencerCost,
    isCampaignType,
    isPlatform,
    postCampaignManagerInfluencer,
    postCampaignManagerInfluencerCost,
    WriteCampaignManagerInfluencerResponse,
} from '@round/api';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import { paymentTermOptions, preferredPaymentOptions } from '../../../../utils/constants';
import InfluencerForm, { CampaignManagerInfluencerFormValues } from './influencerForm/CampaignManagerInfluencerForm';
import { FormikHelpers } from 'formik';
import { mapApiErrorsToFormikErrors } from '../../../../utils/utits';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { showNotification } from '../../../../utils/showNotification';
import styles from './InfluencerModal.module.css';
import { Modal } from '@round/ui-kit';

type CreateInfluencerModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (data: { influencer: WriteCampaignManagerInfluencerResponse; costs: InfluencerCost[] }) => void;
};

const CreateInfluencerModal = ({ onCreated, onClose, isOpen }: CreateInfluencerModalProps) => {
    const { currencies } = useNonNullContext(OptionsContext);

    const initialValues: CampaignManagerInfluencerFormValues = {
        name: '',
        email: '',
        is_available_for_booking: true,
        payment_terms: paymentTermOptions.find((o) => o.value === 'client_terms'),
        preferred_payment_method: preferredPaymentOptions.find((o) => o.value === 'bank_transfer'),
    };

    const handleSubmit = useCallback(
        async (
            values: CampaignManagerInfluencerFormValues,
            { setSubmitting, setErrors }: FormikHelpers<CampaignManagerInfluencerFormValues>
        ) => {
            try {
                setSubmitting(true);
                const influencerResponse = await postCampaignManagerInfluencer({
                    name: values.name,
                    tiktok_username: values.tiktok_username || null,
                    instagram_username: values.instagram_username || null,
                    youtube_username: values.youtube_username || null,
                    content_tags: values.content_tags?.length
                        ? values.content_tags?.map((tag) => Number(tag.value))
                        : [],
                    location: values.location?.value ?? null,
                    first_name: values.first_name,
                    last_name: values.last_name,
                    phone: values.phone,
                    email: values.email,
                    birthday: values.birthday ? values.birthday.split('/').reverse().join('-') : null,
                    address_line_1: values.address_line_1,
                    address_region: values.address_region,
                    address_city: values.address_city,
                    address_postcode: values.address_postcode,
                    bank_name: values.bank_name,
                    bank_account_number: values.bank_account_number,
                    bank_sort_code: values.bank_sort_code,
                    payment_terms: values.payment_terms?.value,
                    preferred_currency: values.preferred_currency?.value ?? null,
                    is_available_for_booking: values.is_available_for_booking,
                    paypal_email_address: values.paypal_email_address,
                    preferred_payment_method: values.preferred_payment_method?.value,
                    contact_details: values?.contact_details,
                    roster_share_agency: values?.roster_share_agency?.value ?? null,
                });

                if (influencerResponse.status === 400) {
                    setErrors(mapApiErrorsToFormikErrors(influencerResponse.data));
                    return;
                }

                const influencer = influencerResponse.data;
                let costs: InfluencerCost[] = [];
                if (values.costs) {
                    const nonNullCostValues = omitBy(values.costs, isNil);
                    const requests = [];
                    for (const platform in nonNullCostValues) {
                        for (const campaignType in nonNullCostValues[platform]) {
                            for (const cost in nonNullCostValues[platform][campaignType]) {
                                if (cost === '') {
                                    continue;
                                }
                                const currencyId = currencies.find((curr) => curr.name === cost.toUpperCase())?.id;
                                if (!currencyId) {
                                    throw new Error('Couldnt find requested currency');
                                }

                                if (isPlatform(platform) && isCampaignType(campaignType)) {
                                    requests.push(
                                        postCampaignManagerInfluencerCost({
                                            influencer: influencerResponse.data.id,
                                            cost: Number(nonNullCostValues[platform][campaignType][cost]),
                                            campaign_type: campaignType,
                                            platform: platform,
                                            currency: currencyId,
                                        })
                                    );
                                } else {
                                    throw new Error('supplied form values did not meet type specification');
                                }
                            }
                        }
                    }
                    costs = (await Promise.all(requests))
                        .filter((response): response is ApiResponse<InfluencerCost, 201> => response.status === 201)
                        .map((response) => response.data);
                }
                onClose();
                onCreated({ influencer, costs });
            } catch {
                showNotification('Could not perform operation', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [onClose, onCreated, currencies]
    );

    return (
        <Modal className={styles.modal} isOpen={isOpen} onClose={onClose} closeOnOverlayClick>
            <InfluencerForm handleSubmit={handleSubmit} initialValues={initialValues} />
        </Modal>
    );
};

export default CreateInfluencerModal;
